import React, { useEffect, useState } from "react";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { ReactComponent as DropdownIcon } from "../../../../assets/icons/paraplanningDashboard/ArrowDropdown.svg";
import { db } from "../../../../backend/firebaseConfig";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import ProgressHistory from "../../../Marketing/MarketingDashabord/components/ProgressHistory";
import { PostCard } from "../HelperFunctions/HelperFunctions";
import { RATE_FIELDS } from "./rateFields";
import "../../../../css/Adviser/AdviserDetailsPage.css";

const EMPTY_VALUE = "-";
const HISTORY_TABS = [
  { id: "editLogs", label: "Edit Logs" },
  { id: "progressHistory", label: "Fees History" },
];

const FIELD_DETAILS = {
  ...Object.fromEntries(
    RATE_FIELDS.map((field) => [
      field.key,
      { label: field.label, type: field.type },
    ]),
  ),
  paidBy: { label: "Paid By", type: "text" },
  monthlyFeePermission: { label: "Monthly Fee Permission", type: "text" },
  monthlyFeeFromDate: { label: "Monthly Fees From Date", type: "date" },
  monthlyFeeToDate: { label: "Monthly Fees To Date", type: "date" },
  payingFor: { label: "Paying For", type: "text" },
  totalMonthlyFeesCharged: {
    label: "Total Monthly Fees Charged",
    type: "currency",
  },
  notes: { label: "Notes", type: "text" },
  adviserSortCode: { label: "Adviser Sort Code", type: "text" },
  adviserAccountNumber: { label: "Adviser Account Number", type: "text" },
  adviserAccountHolder: { label: "Adviser Account Holder", type: "text" },
  arSortCode: { label: "Group Sort Code", type: "text" },
  arAccountNumber: { label: "Group Account Number", type: "text" },
  arAccountHolder: { label: "Group Account Holder", type: "text" },
  statementPassword: { label: "Statement Password", type: "password" },
  sendCommissionPaymentsTo: {
    label: "Adviser to Send Commission Payments",
    type: "text",
  },
  emailCommissionStatementsTo: {
    label: "Send Details",
    type: "text",
  },
  emailCommissionStatementsCc: {
    label: "CC",
    type: "text",
  },
};

const RATE_LOG_FIELDS = new Set([
  ...RATE_FIELDS.map((field) => field.key),
  "paidBy",
  "monthlyFeePermission",
  "monthlyFeeFromDate",
  "monthlyFeeToDate",
  "payingFor",
  "totalMonthlyFeesCharged",
  "notes",
]);

const BANK_LOG_FIELDS = new Set([
  "adviserSortCode",
  "adviserAccountNumber",
  "adviserAccountHolder",
  "arSortCode",
  "arAccountNumber",
  "arAccountHolder",
  "statementPassword",
  "sendCommissionPaymentsTo",
  "emailCommissionStatementsTo",
  "emailCommissionStatementsCc",
]);

const LOG_TAB_DETAILS = {
  rates: {
    title: "IFA Rates History",
    ariaLabel: "IFA rates progress history",
    emptyMessage:
      "No IFA rates edit logs found yet. New changes will appear here after saving.",
    fields: RATE_LOG_FIELDS,
  },
  bankDetails: {
    title: "Bank & Password Details History",
    ariaLabel: "Bank and password details progress history",
    emptyMessage:
      "No bank or password detail edit logs found yet. New changes will appear here after saving.",
    fields: BANK_LOG_FIELDS,
  },
};

const formatDateTime = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatValue = (value, fieldPath) => {
  if (value === "" || value === null || value === undefined) {
    return EMPTY_VALUE;
  }

  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => {
        if (item && typeof item === "object") {
          return [item.name, item.email, item.monthlyFee]
            .filter((entry) => entry !== "" && entry !== null && entry !== undefined)
            .join(" | ");
        }

        return String(item || "").trim();
      })
      .filter(Boolean)
      .join(", ");

    return formatted || EMPTY_VALUE;
  }

  const field = FIELD_DETAILS[fieldPath] || {};
  const number = Number(value);

  if (field.type === "password") return "********";

  if (field.type === "date") {
    const [year, month] = String(value).split("-");
    if (!year || !month) return String(value);

    return new Date(`${year}-${month}-01T12:00:00`).toLocaleDateString(
      "en-GB",
      { month: "short", year: "numeric" },
    );
  }

  if (fieldPath === "monthlyFees" || field.type === "currency") {
    return Number.isFinite(number) ? `£${number.toFixed(2)}` : String(value);
  }

  if (field.type === "percentage" && Number.isFinite(number)) {
    return `${number <= 1 ? number * 100 : number}%`;
  }

  return String(value);
};

const getLogChanges = (log = {}) => {
  if (Array.isArray(log.changes) && log.changes.length > 0) {
    return log.changes;
  }

  return Object.entries(log.rates || {})
    .filter(([fieldPath]) => FIELD_DETAILS[fieldPath])
    .map(([fieldPath, newValue]) => ({
      fieldPath,
      previousValue: "",
      newValue,
    }));
};

export default function RatesEditLogs({ adviserId, activeTab = "rates" }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [historyTab, setHistoryTab] = useState("editLogs");
  const tabDetails = LOG_TAB_DETAILS[activeTab] || LOG_TAB_DETAILS.rates;
  const visibleLogs = logs
    .map((log) => ({
      ...log,
      visibleChanges: getLogChanges(log).filter((change) =>
        tabDetails.fields.has(change.fieldPath),
      ),
    }))
    .filter((log) => log.visibleChanges.length > 0);
  const historyItems = visibleLogs.flatMap((log) => {
    const changedAt = log.updatedAt || log.editedAt;
    const monthlyFeeFields = new Set([
      "monthlyFees",
      "monthlyFeeFromDate",
      "monthlyFeeToDate",
    ]);
    const hasMonthlyFeeChange = log.visibleChanges.some((change) =>
      monthlyFeeFields.has(change.fieldPath),
    );
    if (!hasMonthlyFeeChange) return [];

    const savedRates = log.rates || {};
    const feeChange = log.visibleChanges.find(
      (change) => change.fieldPath === "monthlyFees",
    );
    const fromDateChange = log.visibleChanges.find(
      (change) => change.fieldPath === "monthlyFeeFromDate",
    );
    const toDateChange = log.visibleChanges.find(
      (change) => change.fieldPath === "monthlyFeeToDate",
    );
    const fee = savedRates.monthlyFees ?? feeChange?.newValue ?? "";
    const fromDate =
      savedRates.monthlyFeeFromDate ?? fromDateChange?.newValue ?? "";
    const toDate = savedRates.monthlyFeeToDate ?? toDateChange?.newValue ?? "";

    return [{
      id: log.id,
      label: "Monthly fee updated",
      tone: "blue",
      createdAt: changedAt,
      createdAtMs: changedAt?.toDate ? null : changedAt,
      userDisplayName:
        log.updatedByName || log.updatedBy || log.editedBy || "Unknown user",
      description: `From: ${formatValue(
        fromDate,
        "monthlyFeeFromDate",
      )} | To: ${formatValue(toDate, "monthlyFeeToDate")} | Fee: ${formatValue(
        fee,
        "monthlyFees",
      )}`,
    }];
  });

  useEffect(() => {
    if (!adviserId || !open) {
      setLogs([]);
      setLoading(Boolean(adviserId));
      return undefined;
    }

    setLoading(true);

    const logsRef = collection(
      db,
      "Authorized Members",
      "Advisers",
      "Data",
      adviserId,
      "Rates",
      "Data",
      "Edit Logs",
    );
    const logsQuery = query(logsRef, orderBy("editedAt", "desc"), limit(50));

    return onSnapshot(
      logsQuery,
      (snapshot) => {
        setLogs(
          snapshot.docs.map((logSnapshot) => ({
            id: logSnapshot.id,
            ...logSnapshot.data(),
          })),
        );
        setLoading(false);
      },
      (error) => {
        console.error("Failed to load rate edit logs:", error);
        setLogs([]);
        setLoading(false);
      },
    );
  }, [adviserId, open]);

  return (
    <PostCard
      title={tabDetails.title}
      right={
        <button
          type="button"
          className="table-row-action"
          onClick={() => setOpen((current) => !current)}
          aria-label={open ? "Collapse history" : "Expand history"}
          aria-expanded={open}
          title={open ? "Collapse history" : "Expand history"}
        >
          <DropdownIcon
            aria-hidden
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        </button>
      }
    >
      {open ? (
        <div className="fit-column gap-m">
          <Tabs
            tabs={HISTORY_TABS}
            activeTab={historyTab}
            onTabChange={setHistoryTab}
          />

          {historyTab === "editLogs" ? (
            <>
              {loading ? (
                <p className="Content-Small muted">Loading edit logs...</p>
              ) : null}

              {!loading && visibleLogs.length === 0 ? (
                <p className="Content-Small muted">{tabDetails.emptyMessage}</p>
              ) : null}

              {!loading && visibleLogs.length > 0 ? (
                <div className="lcEditLogsList">
                  {visibleLogs.map((log) => (
                    <div key={log.id} className="lcEditLogItem">
                      <div className="lcEditLogHeader">
                        <span className="FP_Help_Text_Semibold">
                          {log.updatedByName ||
                            log.updatedBy ||
                            log.editedBy ||
                            "Unknown user"}
                        </span>
                        <span className="FP_Help_Text muted">
                          {formatDateTime(log.updatedAt || log.editedAt)}
                        </span>
                      </div>

                      <div className="lcEditLogChanges">
                        {log.visibleChanges.map((change, index) => (
                          <div
                            key={`${log.id}-${change.fieldPath}-${index}`}
                            className="lcEditLogChange"
                          >
                            <div className="FP_Help_Text_Semibold">
                              {FIELD_DETAILS[change.fieldPath]?.label ||
                                change.fieldPath}
                            </div>
                            <div className="FP_Help_Text">
                              <span className="muted">From:</span>{" "}
                              {formatValue(
                                change.previousValue,
                                change.fieldPath,
                              )}
                            </div>
                            <div className="FP_Help_Text">
                              <span className="muted">To:</span>{" "}
                              {formatValue(
                                change.newValue,
                                change.fieldPath,
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <ProgressHistory
              items={historyItems}
              loading={loading}
              title=""
              ariaLabel={tabDetails.ariaLabel}
              emptyMessage={tabDetails.emptyMessage}
              showDuration={false}
              tooltipPosition="right"
            />
          )}
        </div>
      ) : null}
    </PostCard>
  );
}
